import { Grid, GridColumn, IFormHelperProps, PassField, TGridColumnWidth, Text, TextField } from 'telia-front-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import LoginButton from '../components/LoginButton';
import { runInTeliaRumService } from '../util/TeliaOtelUtil';
import RememberMe from './RememberMe';
import {
  PERSONAL_CODE_FIELD_ID,
  PW_FIELD_ID,
  USERNAME_FIELD_ID,
  getHelperErrorMessage,
  helperInErrorState,
  notEmptyFieldHelper,
} from '../util/FieldHelperUtil';
import useStores from '../stores/UseStores';
import { shouldFixModuleRendering } from '../util/ModuleUtil';

const PersonalCodeLogin = observer((): JSX.Element => {
  const {
    personalCodeLoginStore,
    translateStore: { translate },
    uiStore: { componentWidth, showErrorNoticeWithErroredFields, setSelectedTab },
  } = useStores();
  const { personalCodeLoginCredentials, submitting } = personalCodeLoginStore;

  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);
  const [personalCodeHelper, setPersonalCodeHelper] = React.useState<IFormHelperProps | undefined>(undefined);
  const [usernameHelper, setUsernameHelper] = React.useState<IFormHelperProps | undefined>(undefined);
  const [passwordHelper, setPasswordHelper] = React.useState<IFormHelperProps | undefined>(undefined);

  const getPersonalCodeHelper = (forceIsDirty?: boolean) =>
    notEmptyFieldHelper(translate, personalCodeLoginCredentials.personalCode, forceIsDirty ?? hasSubmitted, PERSONAL_CODE_FIELD_ID);
  const getUsernameHelper = (forceIsDirty?: boolean) =>
    notEmptyFieldHelper(translate, personalCodeLoginCredentials.username, forceIsDirty ?? hasSubmitted, USERNAME_FIELD_ID);
  const getPasswordHelper = (forceIsDirty?: boolean) =>
    notEmptyFieldHelper(translate, personalCodeLoginCredentials.password, forceIsDirty ?? hasSubmitted, PW_FIELD_ID);

  React.useEffect(() => {
    setPersonalCodeHelper(getPersonalCodeHelper());
  }, [personalCodeLoginCredentials.personalCode, hasSubmitted]);

  React.useEffect(() => {
    setUsernameHelper(getUsernameHelper());
  }, [personalCodeLoginCredentials.username, hasSubmitted]);

  React.useEffect(() => {
    setPasswordHelper(getPasswordHelper());
  }, [personalCodeLoginCredentials.password, hasSubmitted]);

  const isInvalid = () => {
    return Object.values(personalCodeLoginCredentials).some((field) => !field);
  };

  const handleFormSubmit = (event: React.FormEvent): void => {
    runInTeliaRumService('Autentimine (isikukood)', () => {
      event.preventDefault();
      setHasSubmitted(true);
      if (!isInvalid()) {
        personalCodeLoginStore.handleLogin();
      } else {
        showErrorNoticeWithErroredFields('form.error', [getPersonalCodeHelper(true), getUsernameHelper(true), getPasswordHelper(true)]);
      }
    });
  };

  const gridWidth: TGridColumnWidth[] = ['sm-8', 'md-6', `lg-${shouldFixModuleRendering(componentWidth) ? '10' : '4'}`];

  return (
    <form onSubmit={handleFormSubmit} id="personalCodeLogin">
      <Text>
        <Grid align={['center-sm']}>
          <GridColumn width={gridWidth}>
            <Text>
              <TextField
                label={translate('panel.personalCode')}
                id={PERSONAL_CODE_FIELD_ID}
                value={personalCodeLoginCredentials.personalCode}
                onChange={(value) => (personalCodeLoginCredentials.personalCode = value?.trim())}
                isInvalid={helperInErrorState(personalCodeHelper)}
                errorMessage={getHelperErrorMessage(personalCodeHelper)}
                required={true}
              />
              <TextField
                label={translate('panel.admin.username')}
                id={USERNAME_FIELD_ID}
                value={personalCodeLoginCredentials.username}
                input={{ autoCapitalize: 'none', autoComplete: 'username' }}
                onChange={(value) => (personalCodeLoginCredentials.username = value?.trim())}
                isInvalid={helperInErrorState(usernameHelper)}
                errorMessage={getHelperErrorMessage(usernameHelper)}
                required={true}
              />
              <PassField
                label={translate('panel.admin.password')}
                id={PW_FIELD_ID}
                value={personalCodeLoginCredentials.password}
                onChange={(value) => (personalCodeLoginCredentials.password = value)}
                isInvalid={helperInErrorState(passwordHelper)}
                errorMessage={getHelperErrorMessage(passwordHelper)}
                required={true}
                input={{ autoComplete: 'current-password' }}
              />
              <RememberMe tabItemId="personalCodeLoginTab" />
              <LoginButton isLoading={submitting} disabled={submitting} onClick={() => setSelectedTab('personal')} />
            </Text>
          </GridColumn>
        </Grid>
      </Text>
    </form>
  );
});

export default PersonalCodeLogin;
